import React, { useCallback, useRef, useState } from "react";

export const useScrollState = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position to show/hide scroll buttons
  const checkScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  // Handle scroll button clicks
  const scroll = useCallback(
    (direction: "left" | "right") => {
      if (scrollContainerRef.current) {
        const scrollAmount = 200;
        const newScrollLeft =
          scrollContainerRef.current.scrollLeft +
          (direction === "left" ? -scrollAmount : scrollAmount);

        scrollContainerRef.current.scrollTo({
          left: newScrollLeft,
          behavior: "smooth",
        });

        // Check scroll position after animation
        setTimeout(checkScroll, 300);
      }
    },
    [checkScroll]
  );

  // Initialize scroll check on mount and when dependencies change
  const setupScrollListeners = useCallback(
    (allViewsLength: number) => {
      checkScroll();
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener("scroll", checkScroll);
        window.addEventListener("resize", checkScroll);
        return () => {
          container.removeEventListener("scroll", checkScroll);
          window.removeEventListener("resize", checkScroll);
        };
      }
    },
    [checkScroll]
  );

  return {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    scroll,
    checkScroll,
    setupScrollListeners,
  };
};
